import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedRows, setSelectedItem, setSelectAll, setSelectedHeaders } from '../../components/PerinHealthGridSlice';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import FilterManagementView from '../../components/FilterManagementView'; 
import { closeModal } from '../../components/PerinHealthModelSlice';
import managementStyles from '../ManagementPages.module.css';
import { setRoleInfo, setUpdatingState, resetRegistrationState } from '../../components/AccountManagement/RegistrationSlice';
import useAxios from '../../services/AxiosConfig';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { resetDirty } from '../../slicers/DirtyCheckSlice';
import PerinHealthModal from '../../components/PerinHealthModel';
import { formatDateString } from '../../utils/dateUtils';
import Logger from '../../services/Logger';
import { LogElements, LogAction } from '../../services/LogElements';

const AccountManagement = () => {
    const userId = useSelector((state) => state.userInfo.id);
    const userRoleCategory = useSelector((state) => state.userInfo.roleCategory);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    const navigate = useNavigate();
    const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
    const selectedRows = perinHealthGrid.selectedRows;
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const [activeTab, setActiveTab] = useState('active');
    const [hasLoadedInactivatedAccounts, setHasLoadedInactivatedAccounts] = useState(false);

    const [userList, setUserList] = useState([]);
    const [inactiveUsersList, setInactiveUsers] = useState([]);
    const [inactiveAccountsList, setInactiveAccounts] = useState([]);

    const headers = [
        { key: 'id', label: "ID", sortable:true },
        { key: 'fullName', label: "Name", sortable:true},
        { key: 'email', label: "Email", sortable:true},
        { key: 'fullPhone', label: "Phone", sortable:true},
        { key: 'roleName', label: "Role", sortable:true},
        { key: 'groups', label: 'Group', sortable:true},
        { key: 'subGroups', label: "Location", sortable:true},
        { key: 'teams', label: "Teams", sortable:true},
    ];

    useEffect(() => {
        dispatch(setSelectedItem({}));
        dispatch(resetRegistrationState());
        getUsers();
    }, []);

    useEffect(() => {
        dispatch(setSelectedHeaders(headers));
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'waiting') { 
            getUsersInactivated();
        } else if (activeTab === 'inactive') {
            getDeletedUsers();
        } else {
            getUsers();
        }
    }, [userDateFormat, activeTab]);



    const handleDialogButtonClick = () => {
        dispatch(resetDirty());
        dispatch(closeModal());
    };

    const handleTabSwitch = (tabName) => {
        if (tabName === 'waiting') {
            getUsersInactivated();
        } else if (tabName === 'inactive') {
            getDeletedUsers();
        } else if (tabName === 'active') {
            getUsers();
        }
        dispatch(setSelectedRows([]));
        setActiveTab(tabName);
    };

    const getUsers = () => {
        axios.get(`list/users/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const formatted = formatUsers(responseData.data.data);
                    setUserList(formatted)
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                //console.log(err);
            });
    };

    const getUsersInactivated = () => {
        axios.get(`list/users/waitingForActivation/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const formatted = formatUsers(responseData.data.data);
                    setInactiveUsers(formatted)
                    setHasLoadedInactivatedAccounts(true)
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                //console.log(err);
            });
    };

    const getDeletedUsers = () => {
        // This function is called every time the inactive tab is selected
        //console.log('Fetching deleted users for inactive tab');
        axios.get(`list/users/deleted/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const formatted = formatUsers(responseData.data.data);
                    setInactiveAccounts(formatted);
                } else {
                    console.error('Invalid data format:', responseData.data);
                }
            })
            .catch((err) => {
                console.error('Error fetching deleted users:', err);
                console.error('Error response:', err.response?.data);
                dispatch(
                    showAlert({
                        header: "Error",
                        message: "Failed to load deleted users. Please try again later.",
                        type: 'danger'
                    })
                );
            });
    };
    
    const [roles, setRoles] = useState([]);
    useEffect(() => {
        axios.get(`role/list/${userId}`)
        .then((responseData) => {
            if (Array.isArray(responseData.data.data)) {
                setRoles(responseData.data.data);
            } else {
                console.error('Invalid data format:');
            }
        })
        .catch((err) => {
            //console.log(err);
        });
    }, []);

    const resendVerificationEmail = async (email) => {
        try {
            const response = await axios.request({
                method: 'POST',
                headers: {"Content-Type": "application/json"},
                url: `resend-confirmation-code`,
                data: JSON.stringify({email: email}),
            });
            if (response) {
                dispatch(
                    showAlert({
                        header: "Verification Email Sent",
                        message: "Please check your email for the new link",
                        type: 'success'
                    })
                );
            }
        } catch (error) {
            dispatch(
                showAlert({
                    header: "Could not resend email",
                    message: "Please contact your admin",
                    type: 'danger'
                })
            );
        }
    
    };

    const handleActivationDropdownClick = ({type, item}) => {
        if (type === "resend") {
            resendVerificationEmail(item.email)
        } else {
            return;
        }
    };

    const onRowClickHandler = (rowItem) => {
        if (activeTab === 'inactive') {
            dispatch(
                showAlert({
                    header: "Account Deleted",
                    message: "This account has been deleted. Use the Reactivate button to restore it.",
                    type: 'info'
                })
            );
            return;
        }

        const id = rowItem["id"];
        axios.get('user/'+ id)
            .then((responseData) => {
                if (responseData.data.data) {
                    dispatch(setUpdatingState(responseData.data.data));
                    navigate(`/create-account`);
                } else {
                    console.error('error');
                }
            })
            .catch((err) => {
                //console.log(err);
            });
    };

    const onAddHandler = (selectedRole) => {
        const {name, id, category, organizationId} = selectedRole;
        if ( selectedRole ) {
            dispatch(setRoleInfo({"id":id, "name":name, "category":category, "organizationId":organizationId}));
            navigate(`/create-account`);
        } else {
            return;
        }
    };

    const formatUsers = (data) => {
        const userIdSet = new Set();
        let newFormattedUserList = [];
        if (data.length >= 1) {
            newFormattedUserList = data.reduce((acc, user) => {
                if (!userIdSet.has(user.id)) { 
                    userIdSet.add(user.id);
                    let groups = [];
                    let subGroups = [];
                    let teams = [];
                    let groupIds = [];
                    let subGroupIds = [];
                    let teamIds = [];
                    let organizationIds = [];
                    if (user.groups) {
                        user.groups.forEach(group => {
                            if (group.groupName && !groups.includes(group.groupName)){
                                groups.push(group.groupName);
                                groupIds.push(group.groupId);
                            } 
                            if (group.subGroupName && !subGroups.includes(group.subGroupName)) {
                                subGroups.push(group.subGroupName);
                                subGroupIds.push(group.subGroupId);
                            } 
                            if (group.teamName && !teams.includes(group.teamName)) {
                                teams.push(group.teamName);
                                teamIds.push(group.teamId);
                            }
                            if (group.organizationId){
                                organizationIds.push(group.organizationId);
                            }
                        });
                    }
                    acc.push({
                        ...user,
                        groups: groups, 
                        subGroups: subGroups, 
                        teams: teams,
                        organizationIds: organizationIds,
                        groupIds: groupIds,
                        subGroupIds: subGroupIds,
                        teamIds: teamIds,
                        fullName: user.lastName + ", " + user.firstName,
                        fullPhone: `${user.countryCode ?? ''} ${user.phone ?? ''}`,
                        formattedCreatedDate: formatDateString(user.createdDate, userDateFormat),
                        roleName: user.role.name,
                        profilePictureURL: user.profilePictureURL
                    });
    
                }
                return acc;
            }, []);
        } 
        return newFormattedUserList;
    };


    const onDeleteHandler = () => {
        if (activeTab === 'active') {
            onDeactivateHandler();
        } else if (activeTab === 'waiting') {
            onHardDeleteHandler();
        } else {
            return;
        }
    };

    const onHardDeleteHandler = () => {
        // Log deactivate button click
        Logger.shared.info(
            LogElements.deactivateButton,
            LogAction.clicked,
            'AccountManagement.js',
            `Deleting ${selectedRows.length} inactive account(s)`
        );
        
        const deletePromises = selectedRows.map((user) => {
            const id = user.id;
            return axios.delete(`user/inactive/${id}`);
        });

        Promise.all(deletePromises)
            .then((responses) => {
                dispatch(setSelectedRows([]));
                dispatch(setSelectAll(false));
                getUsers();
                dispatch(closeModal());
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Successfully deleted account`,
                        type: 'success',
                    })
                );
                getUsersInactivated();
            })
            .catch((errors) => {
                dispatch(closeModal());
                const errorMessage = errors.response.data.message ?? "Unable to delete accounts";
                dispatch(
                    showAlert({
                        header: "Error",
                        message: errorMessage,
                        type: 'danger',
                    })
                );
                console.error('Error deleting rows:', errors);
            });
    };

    const onDeactivateHandler = () => {
        // Log deactivate button click
        Logger.shared.info(
            LogElements.deactivateButton,
            LogAction.clicked,
            'AccountManagement.js',
            `Deactivating ${selectedRows.length} account(s)`
        );
        
        const deletePromises = selectedRows.map((user) => {
            const id = user.id;
            return axios.delete(`user/${id}`);
        });

        Promise.all(deletePromises)
            .then((responses) => {
                dispatch(setSelectedRows([]));
                dispatch(setSelectAll(false));
                getUsers();
                dispatch(closeModal());
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Successfully deactivated account`,
                        type: 'success',
                    })
                );
            })
            .catch((errors) => {
                dispatch(closeModal());
                const errorMessage = errors.response.data.message ?? "Unable to delete accounts";
                dispatch(
                    showAlert({
                        header: "Error",
                        message: errorMessage,
                        type: 'danger',
                    })
                );
                console.error('Error deleting rows:', errors);
            });
    };

    const onReactivateHandler = () => {
        // Log reactivate button click
        Logger.shared.info(
            LogElements.reactivateButton,
            LogAction.clicked,
            'AccountManagement.js',
            `Reactivating ${selectedRows.length} account(s)`
        );
        
        //console.log('Selected rows for reactivation:', selectedRows);
        //console.log('Current user ID:', userId);
        
        if (!selectedRows || selectedRows.length === 0) {
            dispatch(
                showAlert({
                    header: "No Selection",
                    message: "Please select at least one account to reactivate.",
                    type: 'warning',
                })
            );
            return;
        }
        
        const validUsers = selectedRows.filter(user => user.id !== userId);
        
        if (validUsers.length === 0) {
            dispatch(
                showAlert({
                    header: "Invalid Selection",
                    message: "Cannot reactivate your own account. Please select other accounts.",
                    type: 'warning',
                })
            );
            return;
        }
        
        const reactivatePromises = validUsers.map((user) => {
            const id = user.id;
            return axios.put(`users/reactivate/${id}`);
        });

        Promise.all(reactivatePromises)
            .then((responses) => {
                dispatch(setSelectedRows([]));
                dispatch(setSelectAll(false));
                getDeletedUsers();
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Successfully reactivated account`,
                        type: 'success',
                    })
                );
            })
            .catch((errors) => {
                console.error('Full error object:', errors);
                const errorMessage = errors.response?.data?.message ?? "Unable to reactivate accounts";
                dispatch(
                    showAlert({
                        header: "Error",
                        message: errorMessage,
                        type: 'danger',
                    })
                );
                console.error('Error reactivating rows:', errors);
            });
    };

    const onArchiveHandler = () => {
        //console.log('Selected rows for archiving:', selectedRows);
        //console.log('Current user ID:', userId);
        
        if (!selectedRows || selectedRows.length === 0) {
            dispatch(
                showAlert({
                    header: "No Selection",
                    message: "Please select at least one account to archive.",
                    type: 'warning',
                })
            );
            return;
        }
        
        const validUsers = selectedRows.filter(user => user.id !== userId);
        
        if (validUsers.length === 0) {
            dispatch(
                showAlert({
                    header: "Invalid Selection",
                    message: "Cannot archive your own account. Please select other accounts.",
                    type: 'warning',
                })
            );
            return;
        }
        
        const archivePromises = validUsers.map((user) => {
            const id = user.id;
            //console.log('Archiving user with ID:', id);
            //console.log('Full user object:', user);
            return axios.put(`users/archive/${id}`);
        });

        Promise.all(archivePromises)
            .then((responses) => {
                dispatch(setSelectedRows([]));
                dispatch(setSelectAll(false));
                getDeletedUsers();
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Successfully archived account(s)`,
                        type: 'success',
                    })
                );
            })
            .catch((errors) => {
                console.error('Full error object:', errors);
                const errorMessage = errors.response?.data?.message ?? "Unable to archive accounts";
                dispatch(
                    showAlert({
                        header: "Error",
                        message: errorMessage,
                        type: 'danger',
                    })
                );
                console.error('Error archiving rows:', errors);
            });
    };

    const tabStyle = (isActive) => ({
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 4,
        paddingBottom: 4,
        background: isActive ? 'rgba(203, 243, 210, 0.30)' : '#F3F3F3',
        borderBottom: isActive ? '3px #006969 solid' : '0.50px #B9B9B9 solid',
        cursor: 'pointer'
    });

    const dropdownOptions = [
        {label:"Resend Verification Email", type:"resend"},
        // {label:"Delete Account", type:"delete"}
    ];

    return (
        <div className={managementStyles.MiddleContent}>
            {userRoleCategory.includes("Admin") &&
                <div style={{width: '100%', borderBottom: '0.50px #B9B9B9 solid', marginTop: '20px'}}>
                <div style={{height: 32, display: 'flex', justifyContent: 'space-between'}}>
                    <div style={{display: 'flex'}}>
                        <div 
                            style={tabStyle(activeTab === 'active')}
                            onClick={() => handleTabSwitch('active')}
                        >
                            <span style={{
                                color: activeTab === 'active' ? 'black' : '#373737',
                                fontSize: 16,
                                fontFamily: 'Roboto',
                                fontWeight: '400',
                                lineHeight: '24px',
                                letterSpacing: 0.04
                            }}>Active</span>
                        </div>
                        <div 
                            style={tabStyle(activeTab === 'waiting')}
                            onClick={() => handleTabSwitch('waiting')}
                        >
                            <span style={{
                                color: activeTab === 'waiting' ? 'black' : '#373737',
                                fontSize: 16,
                                fontFamily: 'Roboto',
                                fontWeight: '400',
                                lineHeight: '24px',
                                letterSpacing: 0.04
                            }}>Waiting for Activation</span>
                        </div>
                        <div 
                            style={tabStyle(activeTab === 'inactive')}
                            onClick={() => handleTabSwitch('inactive')}
                        >
                            <span style={{
                                color: activeTab === 'inactive' ? 'black' : '#373737',
                                fontSize: 16,
                                fontFamily: 'Roboto',
                                fontWeight: '400',
                                lineHeight: '24px',
                                letterSpacing: 0.04
                            }}>Inactive</span>
                        </div>
                    </div>
                </div>
            </div>}
            <FilterManagementView
                isOrganization={true}
                isGroup={true}
                isSubGroup={true}
                isTeam={true}
                addButtonLabel='Create Account'
                onAddHandler={onAddHandler}
                onDeleteHandler={onDeleteHandler}
                onReactivateHandler={onReactivateHandler}
                onArchiveHandler={onArchiveHandler}
                dropdownOptions={roles} 
                showAddButton={activeTab === 'active'}
                showDeleteButton={activeTab !== 'inactive'}
                showReactivateButton={activeTab === 'inactive'}
                showArchiveButton={activeTab === 'inactive'}
                deleteButtonLabel={activeTab === 'active' ? "Deactivate" : "Delete"}
                modelHeader={activeTab === 'active' ? "Deactivate Account?" : "Delete Account?"}
                archiveModalHeader='Archive Account?'
            />
            <div className={managementStyles.GridContainer}>
            <PerinHealthGrid
                dataList={activeTab === 'active' ? userList : activeTab === 'waiting' ? inactiveUsersList : inactiveAccountsList}
                headers={headers}
                showCheckbox={true}
                showEditIcon={activeTab === 'waiting'}
                onRowValueClick={onRowClickHandler}
                onRowClick={activeTab === 'waiting' ? handleActivationDropdownClick : null}
                onDeleteRow={onDeleteHandler}
                accounts={true}
                showHeaderFilter={true}
                showProfilePicture="fullName"
                gridHeight='100%'
                useChips={true}
                rowValueAction={activeTab === 'active' ? 'fullName' : ''}
                moreEditOptions={activeTab === 'waiting' ?  dropdownOptions : false}
            />
            </div>
            <PerinHealthModal
                modalId={"CreateAccount"}
                handleButtonClick={handleDialogButtonClick}
            />
        </div>
    )
}

export default AccountManagement;
